// Plan 08-04 / Phase 32 Plan 32-03 — DEC-006 reveal cinematic timing
// constants + copy keys.
//
// LOCK: T6 = THE FLOP, T11 = THE RIVER.
//
// History:
//   - Plan 08-04 (Phase 8) originally shipped three reveal kinds:
//     T1 = READY, T6 = THE FLOP, T12 = THE RIVER.
//   - Phase 23 amended DEC-006: THE RIVER moved from T12 to T11 to match
//     RUNE-02's second-growth cadence (cells 48..59 land at end-of-T11).
//   - Plan 32-02 (Phase 32 wave 2) extended `RevealKind` with the t11
//     entry while keeping legacy t1 / t12 so the old RevealCinematic
//     route still type-checked during the transition.
//   - Plan 32-03 (Phase 32 wave 3) DELETES the legacy RevealCinematic +
//     prunes the legacy t1 / t12 entries from the union + timing map +
//     fallback strings. The FSM-driven FlopReveal + RiverReveal absorb
//     the only two reveal lanes that survive.
//
// The poker-river-style variant remains the REJECTED naming per DEC-006 —
// see grep gate in timing.test.ts (the rejected variant must NOT appear
// anywhere under apps/web/src/, including in comments).
//
// Cube cascade cadence (UI-SPEC + RUNE-02):
//   - T6  → 12 cells (first growth wave at end-of-T6;  cells 36..47)
//   - T11 → 12 cells (second growth wave at end-of-T11; cells 48..59)
//
// Reduced-motion substitute (UI-SPEC §"Reduced-motion substitution table"):
//   - normal  : 2500ms total (`dur-cinematic-long`), staggered cube cascade
//   - reduced : 200ms total simultaneous cross-fade — no cascade, no
//     celebration

/** The two surviving reveal lanes (Plan 32-03 cleanup). */
export type RevealKind = 't6' | 't11'

export const REVEAL_TIMINGS = {
  t6: {
    title_key: 'reveal.t6.title', // 'THE FLOP'
    subtitle_key: 'reveal.t6.subtitle', // 'Twelve new runes.'
    display_token: 'display-lg',
    cube_count: 12, // 12 new cells (RUNE-02 first growth wave)
    fill: 'gradient_hero',
  },
  t11: {
    title_key: 'reveal.t11.title', // 'THE RIVER'
    subtitle_key: 'reveal.t11.subtitle',
    display_token: 'display-lg',
    cube_count: 12, // 12 new cells (RUNE-02 second growth wave)
    fill: 'gradient_hero',
  },
} as const

/** Total cinematic duration in normal-motion mode (`dur-cinematic-long`). */
export const REVEAL_DURATION_MS = 2500
/** Total cinematic duration under reduced motion (substitution-table cross-fade). */
export const REVEAL_DURATION_REDUCED_MS = 200
/** Per-cube tumble stagger (cascade increment). */
export const CUBE_STAGGER_MS = 200
/** Scrim opacity over the board (--color-bg-deep at 75%). */
export const SCRIM_OPACITY = 0.75

/**
 * English fallback strings for the two reveal titles + subtitles. Plan 07
 * (i18n) wraps these in <FormattedMessage id={key}/> from the locale dicts;
 * the fallback is consumed by useRevealFSM consumers (FlopReveal /
 * RiverReveal) when no IntlProvider is mounted (test rigs, dev surfaces).
 *
 * NOTE: T6 subtitle reads "Twelve new runes." (Plan 32-03 corrects the v1.1
 * stale "Six new runes." copy per UI-SPEC §10) — the cube cohort grew to 12
 * cells when RUNE-02 unified growth waves on cells 36..47.
 *
 * The grep gate in timing.test.ts asserts the rejected variant never
 * appears in this string list (or anywhere under apps/web/src/).
 */
export const REVEAL_FALLBACK_STRINGS = {
  t6: { title: 'THE FLOP', subtitle: 'Twelve new runes.' },
  t11: { title: 'THE RIVER', subtitle: 'The court is full.' },
} as const

/** Resolve effective duration for a given motion mode. */
export function revealDurationMs(reducedMotion: boolean): number {
  return reducedMotion ? REVEAL_DURATION_REDUCED_MS : REVEAL_DURATION_MS
}

/** Resolve effective per-cube stagger (zero under reduced motion). */
export function cubeStaggerMs(reducedMotion: boolean): number {
  return reducedMotion ? 0 : CUBE_STAGGER_MS
}
